import { ActionIcon, Badge, Button, Card, Group, Paper, Text } from "@mantine/core";
import { Container, Grid2 as Grid, Grid2Props as GridProps } from "@mui/material";
import { IconEye, IconClock, IconArrowBack } from "@tabler/icons-react";
import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import MegaLoader from "../../Components/MegaLoader";
import { Colors } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import { PaginatedResponse } from "../../Lib/responses";
import { usePerformRequest } from "../../Lib/usePerformRequest";

import "./styles.scss";
import { AttemptProps } from "../../Lib/types/assessment";

interface AssessmentProps {
  id: string;
  lessonID: string;
  title: string;
  courseID: string;
  questions: Array<{
    id: string;
    title: string;
    options: Array<{ label: string; value: string }>;
    answer: string[] | null;
    type: "single" | "multiple";
  }>;
  attempts: Array<{
    studentID: string;
    scores: number[];
  }>;
  isActive: boolean;
  createdAt: number;
}

const CourseAssessments: React.FC = () => {
  const { t } = useTranslation();
  const { lessonID } = useParams();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [page, setPage] = useState(1);
  const [limit] = useState(12);

  const {
    data: assessments,
    isLoading: isLoadingAssessments,
    reloadData: reloadAssessments,
  } = usePerformRequest<PaginatedResponse<AssessmentProps[]>>({
    route: Endpoints.GetAssessmentsUnderLesson(lessonID as string, page, limit).route,
    method: Endpoints.GetAssessmentsUnderLesson(lessonID as string, page, limit).method,
  });

  console.log("These are the assessments", assessments);

  const gridProps: GridProps = {
    size: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 3,
    },
  };

  if (isLoadingAssessments) {
    return <MegaLoader />;
  }

  return (
    <Container maxWidth="lg">
      <div className="course-assessments-container">
        <Group justify="space-between" mb={20}>
          <Text fw={700} fz={24} c={Colors.PurplePrimary}>
            {t("courses.courseAssessments")}
          </Text>
        </Group>

        <Grid container spacing={3}>
          {assessments?.data?.map((assessment) => (
            <Grid {...gridProps} key={assessment.id}>
              <Card shadow="sm" padding="lg" radius="md" withBorder className="assessment-card">
                <Group justify="space-between" mb="xs">
                  <Text fw={500} lineClamp={2}>
                    {assessment.title}
                  </Text>
                  <Badge color={assessment.isActive ? "green" : "red"} variant="light">
                    {assessment.isActive ? t("common.active") : t("common.inactive")}
                  </Badge>
                </Group>

                <Group gap="xs" mb="md">
                  <Badge variant="light" color="blue">
                    {assessment?.questions?.length || 0} {t("courses.questions")}
                  </Badge>
                  {/* <Badge variant="light" color="grape">
                    {assessment.attempts?.length || 0} Attempts
                  </Badge> */}
                </Group>

                <Group gap="xs" mb="md" className="created-date">
                  <IconClock size={14} style={{ opacity: 0.5 }} />
                  <Text size="sm" c="dimmed">
                    {t("common.createdAt")} {dayjs(assessment.createdAt).format("MMM DD, YYYY")}
                  </Text>
                </Group>

                <Button
                  variant="light"
                  color="blue"
                  fullWidth
                  leftSection={<IconEye size={16} />}
                  onClick={() => navigate(`/d/assessment/${assessment.id}`)}
                >
                  {t("assessments.view")}
                </Button>
              </Card>
            </Grid>
          ))}
        </Grid>

        {(!assessments?.data || assessments?.data?.length === 0) && (
          <Paper p={30} mt={20} style={{ textAlign: "center", background: "#f8f9fa" }}>
            <Text fw={500}>{t("assessments.noAssessments")}</Text>
            <Text size="sm" c="dimmed">
              {t("assessments.noAssessments")}
            </Text>
          </Paper>
        )}
      </div>
    </Container>
  );
};

export default CourseAssessments;
